// https://stackoverflow.com/a/50375286
// We can use apollo's version instead of redefining it here as we already use apollo
import { UnionToIntersection } from '@apollo/client/utilities';

// #region IMPLEMENTATION_DETAILS

// Magic constants from graphql-codegen
// https://github.com/dotansimha/graphql-code-generator/blob/%40graphql-codegen/typescript%404.1.1/examples/typescript-esm/src/gql/fragment-masking.ts#L6
const fragmentName = ' $fragmentName';
const fragmentRefs = ' $fragmentRefs';

type FragmentOf_<T> = T extends {
  [fragmentName]?: infer U extends string;
}
  ? { [fragmentRefs]?: { [K in U]: T } }
  : T;

type NonFragmentOf<T> = T & { [fragmentName]?: never };

type isAny<P, T, F> = P & 1 extends 0 ? T : F;

type IntersectArray<T> = T extends Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  ? T
  : T extends Array<any>
    ? Array<T[number]>
    : { [K in keyof T]: isAny<T[K], T[K], IntersectArray<T[K]>> };

type FlattenFragments_<T> = T extends {
  [fragmentRefs]?: infer U;
}
  ? unknown extends U // Detect Record<string,any>
    ? T
    : FlattenFragments_<
        Omit<UnionToIntersection<U[keyof U]>, typeof fragmentName>
      > &
        Omit<{ [K in keyof T]: FlattenFragments_<T[K]> }, typeof fragmentRefs>
  : T extends Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
    ? T
    : T extends object
      ? { [K in keyof T]: FlattenFragments_<T[K]> }
      : T;

type UnmaskFragment<T> =
  T extends Array<infer U>
    ? Array<UnmaskFragment<U>>
    : T extends { [fragmentName]?: never }
      ? T
      : T extends {
            [fragmentRefs]?: infer U;
          }
        ? U[keyof U]
        : T;

// #endregion

/** Enable data masking for a fragment */
export type FragmentOf<T> = FragmentOf_<T>;

/** Allow passing the masked and unmasked fragment  */
export type OptFragmentOf<T> = FragmentOf<T> | NonFragmentOf<T>;

/** Recursively flatten fragments opt out of data masking */
export type FlattenFragments<T> = IntersectArray<FlattenFragments_<T>>;

/** Unmask both `Fragment` and `OptFragment` types */
export function unmaskFragment<T>(from: T): UnmaskFragment<T>;
export function unmaskFragment<T>(
  from: Nullable<T>
): Nullable<UnmaskFragment<T>>;
export function unmaskFragment(from?: any): any {
  return from;
}

/** Recursively flatten fragments opt out of data masking */
export function flattenFragment<T>(frag: T): FlattenFragments<T> {
  return frag as any;
}
